import { useState, useEffect, useCallback, useRef } from 'react';
import {
  Comment,
  ChildCommentsMap,
  CommentsSectionProps,
  UseCommentsSectionReturn
} from './types';
import {
  fetchComments,
  createComment,
  deleteComment
} from '../../services/commentApi';
import type {
  CreateCommentRes,
  FetchCommentsRes
} from '../../services/commentApi';
import {
  fetchChildComments,
  createChildComment,
  deleteChildComment
} from '../../services/childCommentApi';
import { SessionManager } from '../../utils/SessionManager';
import {
  likeComment,
  unlikeComment,
  getCommentLikesCount,
  getCommentLikers,
  getCommentLikeStatus,
  User
} from '../../services/commentLikeApi';
import {
  likeChildComment,
  unlikeChildComment,
  getChildCommentLikesCount,
  getChildCommentLikers,
  getChildCommentLikeStatus
} from '../../services/childCommentLikeApi';
import axiosInstance from '../../services/api';
import { useSnackbar } from '../../contexts/SnackbarProvider';
import { SnackbarType } from '../../components/Snackbar';

/**
 * コメントセクションのカスタムフック
 */
export const useCommentsSection = ({
  videoId,
  onSeekToTime,
  isDeleted = false,
  isExternal = false
}: CommentsSectionProps): UseCommentsSectionReturn => {
  const activeSession = SessionManager.getActiveSession();
  const currentUserId = activeSession ? activeSession.userId : null;

  const [comments, setComments] = useState<Comment[]>([]);
  const [newComment, setNewComment] = useState('');
  const [loading, setLoading] = useState(false);
  const isInitialLoad = useRef(true);
  const [submitting, setSubmitting] = useState(false);
  const [childSubmitting, setChildSubmitting] = useState<Record<string, boolean>>({});

  const [expandedComments, setExpandedComments] = useState<Record<string, boolean>>({});
  const [childComments, setChildComments] = useState<ChildCommentsMap>({});
  const [childCommentInput, setChildCommentInput] = useState<Record<string, string>>({});
  const [showChildInput, setShowChildInput] = useState<Record<string, boolean>>({});

  // いいね状態・数の管理（コメント用・返信用）
  const [likedComments, setLikedComments] = useState<Record<string, boolean>>({});
  const [commentLikeCounts, setCommentLikeCounts] = useState<Record<string, number>>({});
  const [childLikedComments, setChildLikedComments] = useState<Record<string, boolean>>({});
  const [childCommentLikeCounts, setChildCommentLikeCounts] = useState<Record<string, number>>({});

  // モーダル制御用
  const [showLikersModal, setShowLikersModal] = useState(false);
  const [modalLikers, setModalLikers] = useState<User[]>([]);

  // useSnackbar を利用してエラーメッセージを表示
  const showSnackbar = useSnackbar();

  // シーク関数をpropsの変更に応じて更新
  const onSeekToTimeRef = useRef<((seconds: number) => void) | null>(null);
  
  useEffect(() => {
    // 親コンポーネントから渡されたシーク関数を保存
    onSeekToTimeRef.current = onSeekToTime || null;
  }, [onSeekToTime]);

  // シーク処理（時間指定）
  const handleSeekToTime = useCallback((seconds: number): void => {
    if (onSeekToTimeRef.current && typeof onSeekToTimeRef.current === 'function') {
      onSeekToTimeRef.current(seconds);
      window.scrollTo({ top: 60, behavior: 'smooth' });
    } else {
      console.error('シーク関数が未定義です。親コンポーネントから正しく渡されているか確認してください。');
    }
  }, []);

  // コンポーネントがマウントされたときに実行
  useEffect(() => {
    window.onSeekToTime = handleSeekToTime;
    
    return () => {
      delete window.onSeekToTime;
    };
  }, [handleSeekToTime]);

  useEffect(() => {
    const handleTimestampClick = (seconds: number): void => {
      if (onSeekToTimeRef.current && typeof onSeekToTimeRef.current === 'function') {
        onSeekToTimeRef.current(seconds);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        console.error('シーク関数が未定義です。親コンポーネントから正しく渡されているか確認してください。');
      }
    };
    
    window.handleSeekToTime = handleTimestampClick;
    
    return () => {
      delete window.handleSeekToTime;
    };
  }, [onSeekToTime]);

  const fetchCommentsForVideo = useCallback(async (): Promise<FetchCommentsRes> => {
    if (isExternal) {
      const response = await axiosInstance.get<FetchCommentsRes>(
        `/external-videos/${videoId}/comments`
      );
      return response.data;
    }

    return fetchComments(videoId);
  }, [isExternal, videoId]);

  const createCommentForVideo = useCallback(
    async (content: string): Promise<CreateCommentRes> => {
      if (isExternal) {
        const response = await axiosInstance.post<CreateCommentRes>(
          `/external-videos/${videoId}/comments`,
          { content },
          {
            headers: { 'Content-Type': 'application/json' }
          }
        );
        return response.data;
      }

      return createComment({ video_id: videoId, content });
    },
    [isExternal, videoId]
  );

  // コメント一覧を取得（初回用）
  const loadComments = useCallback(async () => {
    try {
      if (isInitialLoad.current) {
        setLoading(true);
      }
      const data = await fetchCommentsForVideo();
      const commentsData = data.comments;
      setComments(commentsData);

      // 初回取得時はいいね数（なければ 0）を初期化
      const initialCommentLikes: Record<string, number> = {};
      const initialChildCommentLikes: Record<string, number> = {};
      commentsData.forEach((comment: Comment) => {
        initialCommentLikes[comment.id] = comment.likeCount || 0;
        if (comment.childComments) {
          comment.childComments.forEach((child) => {
            initialChildCommentLikes[child.id] = child.likeCount || 0;
          });
        }
      });
      setCommentLikeCounts(initialCommentLikes);
      setChildCommentLikeCounts(initialChildCommentLikes);
      isInitialLoad.current = false; // 初回読み込み完了
    } catch (err) {
      console.error('コメント取得エラー:', err);
      showSnackbar('コメントの取得に失敗しました', SnackbarType.ERROR);
    } finally {
      setLoading(false);
    }
  }, [fetchCommentsForVideo]);

  useEffect(() => {
    if (videoId) {
      loadComments();
    }
  }, [videoId, loadComments]);

  // コメント読み込み後、各コメントのいいね状態を取得
  useEffect(() => {
    const fetchLikeStatuses = async () => {
      const statuses: Record<string, boolean> = {};
      for (const comment of comments) {
        try {
          const { liked } = await getCommentLikeStatus(comment.id.toString());
          statuses[comment.id] = liked;
        } catch {
          statuses[comment.id] = false;
        }
      }
      setLikedComments(statuses);
    };

    if (comments.length > 0) {
      fetchLikeStatuses();
    }
  }, [comments]);

  // コメント読み込み後、各コメントのいいね数を取得
  useEffect(() => {
    const fetchLikeCounts = async () => {
      const counts: Record<string, number> = {};
      for (const comment of comments) {
        try {
          const { likesCount } = await getCommentLikesCount(comment.id.toString());
          counts[comment.id] = likesCount;
        } catch {
          counts[comment.id] = 0;
        }
      }
      setCommentLikeCounts(counts);
    };

    if (comments.length > 0) {
      fetchLikeCounts();
    }
  }, [comments]);

  // 初期コメント読み込み後、子コメントがあるコメントの子コメントいいね数を事前取得
  useEffect(() => {
    const fetchInitialChildLikeCounts = async () => {
      const initialChildCommentLikes: Record<string, number> = {};
      
      for (const comment of comments) {
        if (comment.childCount && comment.childCount > 0) {
          try {
            // 子コメント一覧を取得して、いいね数も取得
            const childCommentsRes = await fetchChildComments(comment.id);
            const childCommentsData = childCommentsRes.childComments || childCommentsRes;
            
            // 各子コメントのいいね数を取得
            for (const child of childCommentsData) {
              try {
                const childId = String(child.id);
                const response = await getChildCommentLikesCount(childId);
                initialChildCommentLikes[childId] = response.likesCount;
              } catch (err) {
                console.error(`子コメント(ID=${child.id})いいね数取得エラー:`, err);
                initialChildCommentLikes[String(child.id)] = 0;
              }
            }
          } catch (err) {
            console.error(`コメント(ID=${comment.id})の子コメントいいね数取得エラー:`, err);
          }
        }
      }
      
      if (Object.keys(initialChildCommentLikes).length > 0) {
        setChildCommentLikeCounts(prev => ({
          ...prev,
          ...initialChildCommentLikes
        }));
      }
    };

    if (comments.length > 0) {
      fetchInitialChildLikeCounts();
    }
  }, [comments]); // commentsが読み込まれた時のみ実行

  // 子コメント読み込み後、各子コメントのいいね数を取得
  useEffect(() => {
    const fetchChildLikeCounts = async () => {
      const counts: Record<string, number> = {};
      for (const parentId in childComments) {
        const children = childComments[parentId];
        if (Array.isArray(children)) {
          for (const child of children) {
            try {
              const childId = String(child.id);
              
              const response = await getChildCommentLikesCount(childId);
              counts[childId] = response.likesCount;
            } catch (err) {
              console.error(`子コメント(ID=${child.id})いいね数取得エラー:`, err);
              counts[String(child.id)] = 0;
            }
          }
        }
      }
      setChildCommentLikeCounts(counts);
    };

    if (Object.keys(childComments).length > 0) {
      fetchChildLikeCounts();
    }
  }, [childComments]);

  // 各親コメントに紐づく子コメントのいいね状態を取得
  useEffect(() => {
    const fetchChildLikeStatuses = async () => {
      const statuses: Record<string, boolean> = {};
      for (const parentId in childComments) {
        const children = childComments[parentId];
        if (Array.isArray(children)) {
          for (const child of children) {
            try {
              const { liked } = await getChildCommentLikeStatus(child.id);
              statuses[child.id] = liked;
            } catch {
              statuses[child.id] = false;
            }
          }
        }
      }
      setChildLikedComments(statuses);
    };

    if (Object.keys(childComments).length > 0) {
      fetchChildLikeStatuses();
    }
  }, [childComments]);

  // 親コメント投稿
  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (!newComment.trim() || submitting || isDeleted) return;
    
    setSubmitting(true); // 送信開始
    try {
      const result = await createCommentForVideo(newComment);
      // 送信完了後、新しいコメントをコメント一覧に追加（再読み込みせずに済む）
      if (result) {
        const newCommentData = result.comment;
        setComments(prevComments => [newCommentData, ...prevComments]);
        setCommentLikeCounts(prev => ({
          ...prev,
          [newCommentData.id]: 0 // 新しいコメントのいいね数は0
        }));
      }
      setNewComment('');
      showSnackbar('コメントを投稿しました', SnackbarType.SUCCESS); // 成功通知
    } catch (err) {
      console.error('コメント投稿エラー:', err);
      showSnackbar('コメントの投稿に失敗しました', SnackbarType.ERROR);
    } finally {
      setSubmitting(false); // 送信完了
    }
  };

  // 親コメント削除
  const handleDelete = async (commentId: number): Promise<void> => {
    try {
      await deleteComment(commentId);
      // コメント一覧から削除したコメントを除外（再読み込みせずに済む）
      setComments(prevComments => 
        prevComments.map(comment => 
          comment.id === commentId 
            ? { ...comment, deleted_at: new Date().toISOString() } 
            : comment
        )
      );
      showSnackbar('コメントを削除しました', SnackbarType.SUCCESS); // 削除成功通知
    } catch (err) {
      console.error('コメント削除エラー:', err);
      showSnackbar('コメントの削除に失敗しました', SnackbarType.ERROR);
    }
  };

  // 子コメント削除
  const handleChildDelete = async (childCommentId: number): Promise<void> => {
    try {
      await deleteChildComment(childCommentId);
      // 子コメント一覧から削除したコメントにdeleted_atフラグを付与（再読み込みせずに済む）
      setChildComments(prevChildComments => {
        const newChildComments = { ...prevChildComments };
        // すべての親コメントIDに対してチェック
        Object.keys(newChildComments).forEach(parentId => {
          if (newChildComments[parentId]) {
            newChildComments[parentId] = newChildComments[parentId].map(child =>
              child.id === childCommentId
                ? { ...child, deleted_at: new Date().toISOString() }
                : child
            );
          }
        });
        return newChildComments;
      });
      showSnackbar('返信を削除しました', SnackbarType.SUCCESS); // 削除成功通知
    } catch (err) {
      console.error('子コメント削除エラー:', err);
      showSnackbar('返信の削除に失敗しました', SnackbarType.ERROR);
    }
  };

  // 返信一覧の表示・非表示を切り替え
  const toggleChildComments = async (comment: Comment): Promise<void> => {
    const commentId = comment.id;
    
    // すでに展開されている場合は折りたたむだけ（APIコール無し）
    if (expandedComments[commentId]) {
      setExpandedComments((prev) => ({ ...prev, [commentId]: false }));
      return;
    }
    
    // 展開する場合
    // 子コメントがまだ読み込まれていない場合のみAPIリクエスト
    if (!childComments[commentId]) {
      try {
        // UI上で先に展開状態にすることで、ローディング中の視覚的フィードバックを提供
        setExpandedComments((prev) => ({ ...prev, [commentId]: true }));
        
        // 子コメント読み込み用の一時的なローディング状態（返信ごとの個別ローディング）
        const tempLoading = document.createElement('div');
        tempLoading.textContent = '返信を読み込み中...';
        tempLoading.className = 'comments-loading-indicator';
        tempLoading.id = `temp-loading-${commentId}`;
        
        // 返信エリアを取得して一時的なローディング表示を追加
        const childListElement = document.querySelector(`[data-parent-comment="${commentId}"]`);
        if (childListElement) {
          childListElement.appendChild(tempLoading);
        }
        
        const res = await fetchChildComments(commentId);
        
        // 一時的なローディング表示を削除
        const loadingElement = document.getElementById(`temp-loading-${commentId}`);
        if (loadingElement) {
          loadingElement.remove();
        }
        
        // APIで取得した子コメントを state にセット
        const childCommentsData = res.childComments || res;
        setChildComments((prev) => ({ ...prev, [commentId]: childCommentsData }));
      } catch (error) {
        console.error('返信取得エラー:', error);
        showSnackbar('返信の読み込みに失敗しました', SnackbarType.ERROR);
        setExpandedComments((prev) => ({ ...prev, [commentId]: false }));
      }
    } else {
      // すでに子コメントが読み込まれている場合は、単に表示するだけ
      setExpandedComments((prev) => ({ ...prev, [commentId]: true }));
    }
  };

  // 子コメント投稿
  const handleChildCommentSubmit = async (commentId: number, e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    const content = childCommentInput[commentId] || '';
    if (!content.trim() || childSubmitting[commentId] || isDeleted) return;
    
    setChildSubmitting(prev => ({ ...prev, [commentId]: true })); // 送信開始
    try {
      const res = await createChildComment({ comment_id: commentId, content });
      
      // 子コメント一覧に直接追加
      const newChildComment = res.childComment || res;
      setChildComments((prev) => ({
        ...prev,
        [commentId]: prev[commentId]
          ? [...prev[commentId], newChildComment]
          : [newChildComment]
      }));
      
      // 新しい子コメントのいいね数も初期化
      setChildCommentLikeCounts(prev => ({
        ...prev,
        [newChildComment.id]: 0
      }));
      
      // 対応する親コメントの子コメント数もインクリメント
      setComments(prevComments => 
        prevComments.map(comment => 
          comment.id === commentId 
            ? { ...comment, childCount: (comment.childCount || 0) + 1 } 
            : comment
        )
      );
      
      setExpandedComments((prev) => ({ ...prev, [commentId]: true }));
      setChildCommentInput((prev) => ({ ...prev, [commentId]: '' }));
      showSnackbar('返信を投稿しました', SnackbarType.SUCCESS); // 成功通知
    } catch (err) {
      console.error('返信投稿エラー:', err);
      showSnackbar('返信の投稿に失敗しました', SnackbarType.ERROR);
    } finally {
      setChildSubmitting(prev => ({ ...prev, [commentId]: false })); // 送信完了
    }
  };

  // 返信入力フォームの表示・非表示を切り替え
  const toggleChildInput = (commentId: number): void => {
    setShowChildInput((prev) => ({ ...prev, [commentId]: !prev[commentId] }));
  };

  // 親コメントのいいね・いいね解除
  const handleCommentLike = async (commentId: number): Promise<void> => {
    const previousLikeState = !!likedComments[commentId];
    const previousCount = commentLikeCounts[commentId] || 0;

    try {
      // UI側を先に更新して即時反映
      const newLikeState = !previousLikeState;
      setLikedComments((prev) => ({ ...prev, [commentId]: newLikeState }));
      
      // カウント予測値を先に表示（+1 または -1）
      const predictedCount = newLikeState ? previousCount + 1 : Math.max(0, previousCount - 1);
      setCommentLikeCounts((prev) => ({ ...prev, [commentId]: predictedCount }));
      
      // 実際のAPI呼び出し
      if (newLikeState) {
        await likeComment(commentId.toString());
        showSnackbar('いいねしました', SnackbarType.SUCCESS);
      } else {
        await unlikeComment(commentId.toString());
        showSnackbar('いいねを取り消しました', SnackbarType.DEFAULT);
      }
      
      // 実際のカウントを取得して最終更新
      const countData = await getCommentLikesCount(commentId.toString());
      setCommentLikeCounts((prev) => ({ ...prev, [commentId]: countData.likesCount }));
    } catch (err) {
      console.error('コメントいいねエラー:', err);
      // エラー時は元の状態に戻す
      setLikedComments((prev) => ({ ...prev, [commentId]: previousLikeState }));
      setCommentLikeCounts((prev) => ({ ...prev, [commentId]: previousCount }));
      showSnackbar('コメントのいいね処理に失敗しました', SnackbarType.ERROR);
    }
  };

  // 子コメントのいいね・いいね解除
  const handleChildCommentLike = async (childCommentId: number): Promise<void> => {
    const previousLikeState = !!childLikedComments[childCommentId];
    const previousCount = childCommentLikeCounts[childCommentId] || 0;

    try {
      // UI側を先に更新して即時反映
      const newLikeState = !previousLikeState;
      setChildLikedComments((prev) => ({ ...prev, [childCommentId]: newLikeState }));
      
      // カウント予測値を先に表示
      const predictedCount = newLikeState ? previousCount + 1 : Math.max(0, previousCount - 1);
      setChildCommentLikeCounts((prev) => ({ ...prev, [childCommentId]: predictedCount }));
      
      // 実際のAPI呼び出し
      if (newLikeState) {
        await likeChildComment(childCommentId);
        showSnackbar('いいねしました', SnackbarType.SUCCESS);
      } else {
        await unlikeChildComment(childCommentId);
        showSnackbar('いいねを取り消しました', SnackbarType.DEFAULT);
      }
      
      // 実際のカウントを取得
      const countData = await getChildCommentLikesCount(childCommentId);
      setChildCommentLikeCounts((prev) => ({ ...prev, [childCommentId]: countData.likesCount }));
    } catch (err) {
      console.error('子コメントいいねエラー:', err);
      // エラー時は元の状態に戻す
      setChildLikedComments((prev) => ({ ...prev, [childCommentId]: previousLikeState }));
      setChildCommentLikeCounts((prev) => ({ ...prev, [childCommentId]: previousCount }));
      showSnackbar('返信へのいいね処理に失敗しました', SnackbarType.ERROR);
    }
  };

  // 親コメントの「いいねした人一覧」表示 (モーダル起動)
  const handleShowCommentLikers = async (commentId: number): Promise<void> => {
    try {
      const data = await getCommentLikers(commentId.toString());
      setModalLikers(data.likers || []);
      setShowLikersModal(true);
    } catch {
      showSnackbar('いいねユーザーの取得に失敗しました', SnackbarType.ERROR);
    }
  };

  // 子コメントの「いいねした人一覧」表示 (モーダル起動)
  const handleShowChildCommentLikers = async (childCommentId: number): Promise<void> => {
    try {
      const data = await getChildCommentLikers(childCommentId);
      // IDを確実に文字列に変換
      const likersWithStringIds = data.likers.map(liker => ({
        ...liker,
        id: String(liker.id)
      }));
      setModalLikers(likersWithStringIds);
      setShowLikersModal(true);
    } catch {
      showSnackbar('いいねユーザーの取得に失敗しました', SnackbarType.ERROR);
    }
  };

  // コメントの折り畳み状態を管理
  const [expandedTexts, setExpandedTexts] = useState<Record<string, boolean>>({});
  const [expandedChildTexts, setExpandedChildTexts] = useState<Record<string, boolean>>({});
  
  // コメントの表示・折り畳みを切り替える
  const toggleCommentExpand = (commentId: number): void => {
    setExpandedTexts(prev => ({
      ...prev,
      [commentId]: !prev[commentId]
    }));
  };
  
  // 子コメントの表示・折り畳みを切り替える
  const toggleChildCommentExpand = (commentId: number): void => {
    setExpandedChildTexts(prev => ({
      ...prev,
      [commentId]: !prev[commentId]
    }));
  };
  
  // コメント内容の行数をチェックする関数
  const checkCommentHeight = (element: HTMLDivElement | null, commentId: number, isChild = false): boolean => {
    if (element) {
      // 75pxを超える場合は折り畳み対象（cssのmax-heightに合わせる）
      const shouldCollapse = element.scrollHeight > 75;
      
      if (isChild) {
        if (shouldCollapse && !expandedChildTexts[commentId]) {
          element.classList.add('collapsed');
        } else {
          element.classList.remove('collapsed');
        }
      } else {
        if (shouldCollapse && !expandedTexts[commentId]) {
          element.classList.add('collapsed');
        } else {
          element.classList.remove('collapsed');
        }
      }
      
      return shouldCollapse;
    }
    return false;
  };
  
  // コメント表示後、高さをチェックしてクラスを追加
  const commentRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const childCommentRefs = useRef<Record<string, HTMLDivElement | null>>({});
  
  // 親コメントの高さチェック用（コメント読み込み後）
  useEffect(() => {
    // 少し遅延させて DOM が完全に更新された後に実行
    const timer = setTimeout(() => {
      comments.forEach(comment => {
        if (commentRefs.current[comment.id]) {
          checkCommentHeight(commentRefs.current[comment.id], comment.id);
        }
      });
    }, 100);
    
    return () => clearTimeout(timer);
  }, [comments, expandedTexts]);
  
  // 子コメントの高さチェック用（子コメント表示後）
  useEffect(() => {
    const timer = setTimeout(() => {
      Object.keys(childComments).forEach(parentId => {
        if (expandedComments[parentId] && childComments[parentId]) {
          childComments[parentId].forEach(child => {
            if (childCommentRefs.current[child.id]) {
              checkCommentHeight(childCommentRefs.current[child.id], child.id, true);
            }
          });
        }
      });
    }, 100);
    
    return () => clearTimeout(timer);
  }, [childComments, expandedComments, expandedChildTexts]);

  // モーダルを閉じる
  const handleCloseLikersModal = (): void => {
    setShowLikersModal(false);
  };

  // 子コメント入力更新関数（コンポーネント向けに提供）
  const updateChildCommentInput = (commentId: number, text: string): void => {
    setChildCommentInput(prev => ({ ...prev, [commentId]: text }));
  };

  // 子コメント入力表示更新関数（コンポーネント向けに提供）
  const updateShowChildInput = (commentId: number, show: boolean): void => {
    setShowChildInput(prev => ({ ...prev, [commentId]: show }));
  };

  // キャンセルボタン用ヘルパー関数（コンポーネント向けに提供）
  const handleCancelChildComment = (commentId: number): void => {
    setChildCommentInput(prev => ({ ...prev, [commentId]: '' }));
    setShowChildInput(prev => ({ ...prev, [commentId]: false }));
  };

  return {
    comments,
    newComment,
    loading,
    isInitialLoad: isInitialLoad.current,
    submitting,
    childSubmitting,
    expandedComments,
    childComments,
    childCommentInput,
    showChildInput,
    likedComments,
    commentLikeCounts,
    childLikedComments,
    childCommentLikeCounts,
    showLikersModal,
    modalLikers,
    expandedTexts,
    expandedChildTexts,
    commentRefs,
    childCommentRefs,
    handleSubmit,
    handleDelete,
    handleChildDelete,
    toggleChildComments,
    handleChildCommentSubmit,
    toggleChildInput,
    handleCommentLike,
    handleChildCommentLike,
    handleShowCommentLikers,
    handleShowChildCommentLikers,
    toggleCommentExpand,
    toggleChildCommentExpand,
    checkCommentHeight,
    handleSeekToTime,
    setNewComment,
    handleCloseLikersModal,
    // 追加の関数
    updateChildCommentInput,
    updateShowChildInput,
    handleCancelChildComment
  };
}; 
